import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Newspaper, Plus, Trash2, ArrowUp, ArrowDown, ImagePlus, X,
  FileDown, Mail, Save, Loader2, FolderOpen, Quote, Star,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptySection = () => ({
  _key: Math.random().toString(36).slice(2),
  heading: '', byline: '', body: '', quote: '', photo_urls: [], link_url: '', link_label: '',
});

const defaultMeta = () => ({
  issue_label: '',
  title: 'LIFE Lines',
  subtitle: 'Monthly inspiration, stories, and updates from Project LIFE',
  cover_photo_url: '',
  upcoming_dates: '',
});

export default function NewsletterPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'system_admin' || user?.role === 'team_admin';
  const [meta, setMeta] = useState(defaultMeta());
  const [sections, setSections] = useState([emptySection()]);
  const [pages, setPages] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedList, setSavedList] = useState([]);
  const [loadedId, setLoadedId] = useState(null);
  const [openOpen, setOpenOpen] = useState(false);
  const [storiesOpen, setStoriesOpen] = useState(false);
  const [stories, setStories] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailMode, setEmailMode] = useState('all');
  const [emailList, setEmailList] = useState('');
  const [emailNote, setEmailNote] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const debounceRef = useRef(null);
  const uploadTargetRef = useRef(null); // 'cover' or section _key
  const fileInputRef = useRef(null);

  const buildData = useCallback(() => ({
    ...meta,
    sections: sections.map(({ _key, ...s }) => s),
  }), [meta, sections]);

  const fetchSaved = async () => {
    try {
      const res = await axios.get(`${API}/newsletters`);
      setSavedList(res.data);
    } catch { /* non-critical */ }
  };

  useEffect(() => { fetchSaved(); }, []);

  // debounced live preview
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreviewing(true);
      try {
        const res = await axios.post(`${API}/newsletters/preview`, buildData());
        setPages(res.data.pages || []);
      } catch { /* keep last preview */ }
      setPreviewing(false);
    }, 900);
    return () => clearTimeout(debounceRef.current);
  }, [buildData]);

  const setSection = (key, patch) => {
    setSections(prev => prev.map(s => (s._key === key ? { ...s, ...patch } : s)));
  };

  const moveSection = (key, dir) => {
    setSections(prev => {
      const i = prev.findIndex(s => s._key === key);
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const pickPhoto = (target) => {
    uploadTargetRef.current = target;
    fileInputRef.current?.click();
  };

  const onFilesChosen = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const target = uploadTargetRef.current;
    for (const f of files.slice(0, 6)) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const up = await axios.post(`${API}/templates/upload-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const url = up.data.url;
        if (target === 'cover') {
          setMeta(prev => ({ ...prev, cover_photo_url: url }));
          break;
        }
        setSections(prev => prev.map(s => (s._key === target ? { ...s, photo_urls: [...s.photo_urls, url].slice(0, 8) } : s)));
      } catch {
        toast.error(`Could not upload ${f.name}`);
      }
    }
  };

  const saveIssue = async () => {
    setSaving(true);
    const name = `${meta.title || 'LIFE Lines'}${meta.issue_label ? ` — ${meta.issue_label}` : ''}`;
    try {
      if (loadedId) {
        await axios.put(`${API}/newsletters/${loadedId}`, { name, data: buildData() });
      } else {
        const res = await axios.post(`${API}/newsletters`, { name, data: buildData() });
        setLoadedId(res.data.id);
      }
      toast.success('Newsletter saved');
      fetchSaved();
    } catch {
      toast.error('Could not save the newsletter');
    }
    setSaving(false);
  };

  const openStoryPicker = async () => {
    setStoriesOpen(true);
    try {
      const res = await axios.get(`${API}/success-stories`);
      setStories(res.data);
    } catch {
      toast.error('Could not load success stories');
    }
  };

  const importStory = (story) => {
    const byline = [story.student_name ? `Featuring ${story.student_name}` : '', story.program].filter(Boolean).join(' · ');
    setSections(prev => [...prev, {
      ...emptySection(),
      heading: story.headline || '',
      byline,
      body: story.story || '',
      quote: story.quote || '',
      photo_urls: story.photo_url ? [story.photo_url] : [],
      link_url: story.link_url || '',
      link_label: story.link_label || '',
    }]);
    setStoriesOpen(false);
    toast.success(`Added "${story.headline}" as a new section`);
  };

  const loadIssue = (nl) => {
    const d = nl.data || {};
    setMeta({ ...defaultMeta(), ...d, sections: undefined });
    setSections((d.sections || [emptySection()]).map(s => ({ ...emptySection(), ...s })));
    setLoadedId(nl.id);
    setOpenOpen(false);
    toast.success(`Loaded "${nl.name}"`);
  };

  const deleteIssue = async (id) => {
    try {
      await axios.delete(`${API}/newsletters/${id}`);
      if (id === loadedId) setLoadedId(null);
      fetchSaved();
      toast.success('Deleted');
    } catch {
      toast.error('Could not delete');
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    const attempt = () => axios.post(`${API}/newsletters/render`, buildData(), { responseType: 'blob', timeout: 90000 });
    try {
      let res;
      try {
        res = await attempt();
      } catch {
        await new Promise(r => setTimeout(r, 1200));
        res = await attempt();
      }
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(meta.title || 'LIFE-Lines').replace(/\s+/g, '-')}-newsletter.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not generate the PDF');
    }
    setDownloading(false);
  };

  const sendEmail = async () => {
    setSendingEmail(true);
    try {
      const payload = { data: buildData(), note: emailNote };
      if (emailMode === 'all') payload.send_to_all = true;
      else payload.emails = emailList;
      const res = await axios.post(`${API}/newsletters/email`, payload);
      toast.success(`Newsletter queued to ${res.data.queued} recipient${res.data.queued === 1 ? '' : 's'}`);
      setEmailOpen(false);
      setEmailNote('');
      setEmailList('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the newsletter');
    }
    setSendingEmail(false);
  };

  return (
    <div className="space-y-6" data-testid="newsletter-page">
      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
        onChange={onFilesChosen} data-testid="nl-photo-input" />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
            <Newspaper className="w-6 h-6 text-[#34657f]" /> Newsletter Builder
          </h1>
          <p className="text-sm text-[#5C5C5C] mt-1">Build a "{meta.title || 'LIFE Lines'}" issue — the preview updates as you type.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setOpenOpen(true)} data-testid="nl-open-btn">
            <FolderOpen className="w-4 h-4 mr-1.5" /> Open saved
          </Button>
          <Button variant="outline" onClick={saveIssue} disabled={saving} data-testid="nl-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save
          </Button>
          <Button variant="outline" onClick={downloadPdf} disabled={downloading} data-testid="nl-download-btn">
            {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />} Download PDF
          </Button>
          {isAdmin && (
            <Button className="bg-[#34657f] hover:bg-[#2a5268] text-white" onClick={() => setEmailOpen(true)} data-testid="nl-email-btn">
              <Mail className="w-4 h-4 mr-1.5" /> Email newsletter
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        {/* ------- builder form ------- */}
        <div className="space-y-4">
          <Card className="dashboard-card">
            <CardContent className="p-5 space-y-3">
              <h2 className="font-semibold text-[#1A1A1A]">Masthead</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="nl-issue">Issue date label</Label>
                  <Input id="nl-issue" placeholder="e.g. September, 2026" value={meta.issue_label}
                    onChange={(e) => setMeta(p => ({ ...p, issue_label: e.target.value }))} data-testid="nl-issue-label" />
                </div>
                <div>
                  <Label htmlFor="nl-title">Newsletter title</Label>
                  <Input id="nl-title" value={meta.title}
                    onChange={(e) => setMeta(p => ({ ...p, title: e.target.value }))} data-testid="nl-title" />
                </div>
              </div>
              <div>
                <Label htmlFor="nl-subtitle">Subtitle</Label>
                <Input id="nl-subtitle" value={meta.subtitle}
                  onChange={(e) => setMeta(p => ({ ...p, subtitle: e.target.value }))} data-testid="nl-subtitle" />
              </div>
              <div>
                <Label htmlFor="nl-dates">Important upcoming dates (one per line — shows as a highlighted box at the top)</Label>
                <Textarea id="nl-dates" rows={3} placeholder={'e.g. May 5 — Rotation showcase at Kroger\nMay 12 — IEP planning week\nMay 30 — Graduation ceremony'}
                  value={meta.upcoming_dates}
                  onChange={(e) => setMeta(p => ({ ...p, upcoming_dates: e.target.value }))} data-testid="nl-upcoming-dates" />
              </div>
              <div className="flex items-center gap-3">
                <Button type="button" variant="outline" size="sm" onClick={() => pickPhoto('cover')} data-testid="nl-cover-btn">
                  <ImagePlus className="w-4 h-4 mr-1.5" /> {meta.cover_photo_url ? 'Replace cover photo' : 'Add cover photo'}
                </Button>
                {meta.cover_photo_url && (
                  <button type="button" className="text-xs text-red-600 hover:underline flex items-center gap-1"
                    onClick={() => setMeta(p => ({ ...p, cover_photo_url: '' }))} data-testid="nl-cover-remove">
                    <X className="w-3 h-3" /> Remove cover photo
                  </button>
                )}
              </div>
            </CardContent>
          </Card>

          {sections.map((s, idx) => (
            <Card key={s._key} className="dashboard-card" data-testid={`nl-section-${idx}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-[#1A1A1A]">Section {idx + 1}</h2>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" disabled={idx === 0} onClick={() => moveSection(s._key, -1)}
                      aria-label={`Move section ${idx + 1} up`} data-testid={`nl-section-up-${idx}`}>
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" disabled={idx === sections.length - 1} onClick={() => moveSection(s._key, 1)}
                      aria-label={`Move section ${idx + 1} down`} data-testid={`nl-section-down-${idx}`}>
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" disabled={sections.length === 1}
                      onClick={() => setSections(prev => prev.filter(x => x._key !== s._key))}
                      aria-label={`Delete section ${idx + 1}`} data-testid={`nl-section-delete-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Heading</Label>
                    <Input placeholder="e.g. A catchy article title" value={s.heading}
                      onChange={(e) => setSection(s._key, { heading: e.target.value })} data-testid={`nl-heading-${idx}`} />
                  </div>
                  <div>
                    <Label>Byline (optional)</Label>
                    <Input placeholder="e.g. By Student Name, Year Level" value={s.byline}
                      onChange={(e) => setSection(s._key, { byline: e.target.value })} data-testid={`nl-byline-${idx}`} />
                  </div>
                </div>
                <div>
                  <Label>Article text</Label>
                  <Textarea rows={5} placeholder="Write the article here…" value={s.body}
                    onChange={(e) => setSection(s._key, { body: e.target.value })} data-testid={`nl-body-${idx}`} />
                </div>
                <div>
                  <Label className="flex items-center gap-1"><Quote className="w-3.5 h-3.5" /> Pull-quote (optional — shows as the blue quote box)</Label>
                  <Input placeholder="Add a quote or key phrase to emphasize…" value={s.quote}
                    onChange={(e) => setSection(s._key, { quote: e.target.value })} data-testid={`nl-quote-${idx}`} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Link URL (optional — clickable in the PDF)</Label>
                    <Input placeholder="e.g., https://local-news.com/article" value={s.link_url}
                      onChange={(e) => setSection(s._key, { link_url: e.target.value })} data-testid={`nl-link-url-${idx}`} />
                  </div>
                  <div>
                    <Label>Link text (optional)</Label>
                    <Input placeholder="e.g., Read the full story" value={s.link_label}
                      onChange={(e) => setSection(s._key, { link_label: e.target.value })} data-testid={`nl-link-label-${idx}`} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => pickPhoto(s._key)} data-testid={`nl-photos-btn-${idx}`}>
                    <ImagePlus className="w-4 h-4 mr-1.5" /> Add photos ({s.photo_urls.length})
                  </Button>
                  {s.photo_urls.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {s.photo_urls.map((u, pi) => (
                        <div key={u + pi} className="relative">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt={`Section ${idx + 1} photo ${pi + 1}`}
                            className="w-16 h-16 rounded-md object-cover border border-slate-200" />
                          <button type="button"
                            className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-4.5 h-4.5 p-0.5"
                            onClick={() => setSection(s._key, { photo_urls: s.photo_urls.filter((_, x) => x !== pi) })}
                            aria-label="Remove photo" data-testid={`nl-photo-remove-${idx}-${pi}`}>
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button variant="outline" className="w-full border-dashed" onClick={() => setSections(prev => [...prev, emptySection()])}
              data-testid="nl-add-section-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add section
            </Button>
            <Button variant="outline" className="w-full border-dashed" onClick={openStoryPicker}
              data-testid="nl-import-story-btn">
              <Star className="w-4 h-4 mr-1.5 text-[#e87722]" /> Add from Success Story
            </Button>
          </div>
        </div>

        {/* ------- live preview ------- */}
        <div className="xl:sticky xl:top-20 space-y-3" data-testid="nl-preview-panel">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-[#1A1A1A]">Preview {pages.length > 0 && `· ${pages.length} page${pages.length === 1 ? '' : 's'}`}</h2>
            {previewing && <Loader2 className="w-4 h-4 animate-spin text-[#34657f]" />}
          </div>
          <div className="space-y-4 max-h-[78vh] overflow-y-auto pr-1">
            {pages.length === 0 && !previewing && (
              <div className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-sm text-[#5C5C5C]">
                Start typing — your newsletter preview will appear here.
              </div>
            )}
            {pages.map((p, i) => (
              <img key={i} src={`data:image/png;base64,${p}`} alt={`Newsletter page ${i + 1}`}
                className="w-full rounded-md border border-slate-200 shadow-sm" data-testid={`nl-preview-page-${i}`} />
            ))}
          </div>
        </div>
      </div>

      {/* open saved dialog */}
      <Dialog open={openOpen} onOpenChange={setOpenOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Saved newsletters</DialogTitle>
            <DialogDescription>Open a saved issue to keep editing, or delete old drafts.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {savedList.length === 0 && <p className="text-sm text-[#5C5C5C]">Nothing saved yet.</p>}
            {savedList.map(nl => (
              <div key={nl.id} className="flex items-center justify-between gap-2 border border-slate-200 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <p className="font-medium text-sm text-[#1A1A1A] truncate">{nl.name}</p>
                  <p className="text-xs text-[#5C5C5C]">Updated {new Date(nl.updated_at).toLocaleDateString()} · {nl.created_by_name}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => loadIssue(nl)} data-testid={`nl-load-${nl.id}`}>Open</Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteIssue(nl.id)}
                    aria-label={`Delete ${nl.name}`} data-testid={`nl-delete-${nl.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* success story picker */}
      <Dialog open={storiesOpen} onOpenChange={setStoriesOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add a Success Story</DialogTitle>
            <DialogDescription>Pick a story — it becomes a new newsletter section (heading, text, quote and photo included) that you can still edit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {stories.length === 0 && <p className="text-sm text-[#5C5C5C]">No success stories yet.</p>}
            {stories.map(st => (
              <button key={st.id} type="button" onClick={() => importStory(st)}
                className="w-full flex items-center gap-3 border border-slate-200 rounded-lg px-3 py-2 text-left hover:border-[#34657f] hover:bg-[#f0f5f8] transition-colors"
                data-testid={`nl-story-${st.id}`}>
                {st.photo_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${st.photo_url}`} alt=""
                    className="w-11 h-11 rounded-md object-cover shrink-0" />
                ) : (
                  <div className="w-11 h-11 rounded-md bg-[#f0f5f8] flex items-center justify-center shrink-0">
                    <Star className="w-5 h-5 text-[#e87722]" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-medium text-sm text-[#1A1A1A] truncate">{st.headline}</p>
                  <p className="text-xs text-[#5C5C5C] truncate">{[st.student_name, st.program].filter(Boolean).join(' · ')}</p>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Email this newsletter</DialogTitle>
            <DialogDescription>The current issue is attached as a PDF. Emails send in the background.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <RadioGroup value={emailMode} onValueChange={setEmailMode}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="nl-email-all" data-testid="nl-email-all" />
                <Label htmlFor="nl-email-all">Send to all active LIFE Link members</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="list" id="nl-email-list" data-testid="nl-email-list-radio" />
                <Label htmlFor="nl-email-list">Send to specific email addresses</Label>
              </div>
            </RadioGroup>
            {emailMode === 'list' && (
              <Textarea rows={3} placeholder="Separate addresses with commas or new lines…" value={emailList}
                onChange={(e) => setEmailList(e.target.value)} data-testid="nl-email-list-input" />
            )}
            <div>
              <Label htmlFor="nl-email-note">Note (optional)</Label>
              <Textarea id="nl-email-note" rows={2} placeholder="A short message shown in the email…" value={emailNote}
                onChange={(e) => setEmailNote(e.target.value)} data-testid="nl-email-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button className="bg-[#34657f] hover:bg-[#2a5268] text-white" onClick={sendEmail} disabled={sendingEmail}
              data-testid="nl-email-send-btn">
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Mail className="w-4 h-4 mr-1.5" />} Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
